import { useTranslation } from 'react-i18next';
import {
  IconArrowRight,
  IconBriefcase,
  IconCalendar,
  IconClock,
  IconUserCircle,
} from '../icons/BookingFlowIcons';
import { formatBookingDateShort, localeTagFromLang } from '../../utils/bookingLocaleDisplay';
import { stripEmojiUnicode } from '../../utils/stripEmoji';
import './BookingComponents.css';

interface Resource {
  id: string;
  name: string;
  description?: string;
}

interface ResourceSelectionProps {
  resources: Resource[];
  service?: any;
  date?: string;
  formattedDate?: string;
  time?: string;
  allowAutoAssign?: boolean;
  onSelect: (resourceId: string) => void;
  onAutoAssign?: () => void;
  language?: string;
  i18n?: { title?: string; subtitle?: string; selectButtonLabel?: string; autoAssignLabel?: string };
}

export function ResourceSelection({ 
  resources, 
  service,
  date,
  formattedDate,
  time,
  allowAutoAssign,
  onSelect,
  onAutoAssign,
  language = 'es',
  i18n,
}: ResourceSelectionProps) {
  const { t } = useTranslation();
  const tl = (key: string) => t(key, { lng: language });
  const stripMd = (s: string) => s.replace(/\*+/g, '').trim();
  const localeTag = localeTagFromLang(language);
  const isoDate = date?.slice(0, 10);
  const dateLabel =
    isoDate && /^\d{4}-\d{2}-\d{2}$/.test(isoDate)
      ? formatBookingDateShort(isoDate, localeTag)
      : formattedDate
        ? stripEmojiUnicode(formattedDate)
        : '';
  const title = stripEmojiUnicode(i18n?.title ? stripMd(i18n.title) : tl('gym.resource_title'));
  const subtitle = stripEmojiUnicode(i18n?.subtitle?.trim() || tl('gym.resource_subtitle'));
  const selectLabel = stripEmojiUnicode(i18n?.selectButtonLabel?.trim() || tl('booking.select'));
  const autoAssignLabel = stripEmojiUnicode(i18n?.autoAssignLabel?.trim() || tl('gym.auto_assign'));
  return (
    <div className="booking-step booking-step--flow-left">
      <h2 className="booking-step-heading">{title}</h2>
      {(service || dateLabel || time) && (
        <div className="booking-context">
          {service && (
            <div className="context-item">
              <span className="context-icon" aria-hidden>
                <IconBriefcase />
              </span>
              <span>
                {tl('booking.service')}:{' '}
                <strong>{stripEmojiUnicode(String(service.name || service))}</strong>
              </span>
            </div>
          )}
          {dateLabel ? (
            <div className="context-item">
              <span className="context-icon" aria-hidden>
                <IconCalendar />
              </span>
              <span>
                {tl('booking.date')}: <strong>{dateLabel}</strong>
              </span>
            </div>
          ) : null}
          {time && (
            <div className="context-item">
              <span className="context-icon" aria-hidden>
                <IconClock />
              </span>
              <span>
                {tl('booking.time')}: <strong>{time}</strong>
              </span>
            </div>
          )}
        </div>
      )}
      <p className="resource-subtitle">{subtitle}</p>
      <div className="resource-list">
        {resources.map((resource) => (
          <div
            key={resource.id}
            className="resource-card"
            onClick={() => onSelect(resource.id)}
          >
            <div className="resource-info">
              <h3>{stripEmojiUnicode(resource.name)}</h3>
              {resource.description ? (
                <p>{stripEmojiUnicode(resource.description)}</p>
              ) : null}
            </div>
            <button
              type="button"
              className="resource-select-btn"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(resource.id);
              }}
            >
              <span>{selectLabel}</span>
              <IconArrowRight className="resource-select-btn-icon" width={16} height={16} aria-hidden />
            </button>
          </div>
        ))}
        {allowAutoAssign && onAutoAssign && resources.length > 1 && (
          <div
            className="resource-card resource-auto-assign"
            onClick={onAutoAssign}
          >
            <div className="resource-info">
              <h3 className="resource-card-title-with-icon">
                <IconUserCircle className="resource-card-title-icon" aria-hidden />
                {autoAssignLabel}
              </h3>
              <p>{stripEmojiUnicode(tl('gym.auto_assign_desc'))}</p>
            </div>
            <button
              type="button"
              className="resource-select-btn"
              onClick={(e) => {
                e.stopPropagation();
                onAutoAssign();
              }}
            >
              <span>{selectLabel}</span>
              <IconArrowRight className="resource-select-btn-icon" width={16} height={16} aria-hidden />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
